import {testIdProps} from '@github-ui/test-id-props'
import {Box, Heading, Text} from '@primer/react'
import {useMemo, useRef} from 'react'
import {Transition, type TransitionStatus} from 'react-transition-group'
import type {CSSObject} from 'styled-components'

import type {MemexColumnDataType} from '../../../api/columns/contracts/memex-column'
import {VerticalSortableContext} from '../../../helpers/dnd-kit/vertical-sortable-context'
import {CommitState} from '../../../hooks/use-autosave'
import {useSingleSelectOptions} from '../../../hooks/use-single-select-options'
import type {ColumnModelForDataType} from '../../../models/column-model'
import {errorStyle, RequestStateIcon} from '../../common/state-style-decorators'
import {SingleSelectForm} from './single-select-form'

type ColumnSettingsViewProps = {
  column: ColumnModelForDataType<typeof MemexColumnDataType.SingleSelect>
}

const duration = 300

const defaultStyle = {
  opacity: 0,
}

const transitionStyles: Record<TransitionStatus, CSSObject> = {
  entering: {opacity: 1},
  entered: {opacity: 1},
  exiting: {opacity: 0, transition: `opacity ${duration}ms ease-in-out`},
  exited: {opacity: 0},
  unmounted: {opacity: 0},
}

export const SingleSelectOptions: React.FC<ColumnSettingsViewProps> = ({column: externalColumn}) => {
  const {
    commitState,
    // column settings
    options,
    onDrop,
    addOption,
    canRemoveOption,
    removeOption,
    updateOption,
    // error message
    errorMessage,
  } = useSingleSelectOptions({externalColumn})

  const saveNodeRef = useRef<HTMLDivElement | null>(null)

  const additionalStyle = commitState === CommitState.Failed ? errorStyle : {}

  const optionIds = useMemo(() => options.map(option => option.id), [options])
  const titleMap = useMemo(() => {
    const map: Record<string, string> = {}
    for (const option of options) {
      map[option.id] = option.name
    }
    return map
  }, [options])

  return (
    <VerticalSortableContext onDrop={onDrop} itemIds={optionIds} titleMap={titleMap}>
      <Heading as="h3" sx={{fontSize: 1, mt: 4, mb: 2}}>
        Options
      </Heading>
      <SingleSelectForm
        options={options}
        onAddOption={addOption}
        onUpdateOption={updateOption}
        onRemoveOption={removeOption}
        canRemoveOption={canRemoveOption}
        sx={additionalStyle}
      />
      <Box sx={{display: 'flex', alignItems: 'center'}}>
        <>
          <Transition in={commitState === CommitState.Successful} nodeRef={saveNodeRef} timeout={500}>
            {state => (
              <Text
                sx={{pl: 2, mt: 2}}
                ref={saveNodeRef}
                style={{
                  ...defaultStyle,
                  ...transitionStyles[state],
                }}
              >
                <RequestStateIcon status={CommitState.Successful} />
                <Text sx={{ml: 2}} {...testIdProps('column-settings-saved-message')}>
                  Saved!
                </Text>
              </Text>
            )}
          </Transition>
          {commitState === CommitState.Failed && (
            <>
              <RequestStateIcon status={CommitState.Failed} />
              <Text sx={{ml: 2}} {...testIdProps('single-select-options-error-box')}>
                {errorMessage.current}
              </Text>
            </>
          )}
        </>
      </Box>
    </VerticalSortableContext>
  )
}

try{ SingleSelectOptions.displayName ||= 'SingleSelectOptions' } catch {}